import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import ProductConfirmation from '../../components/ProductConfirmation/ProductConfirmation';
import OrderDetailsDocuments from '../../components/OrderDetailsDocuments/OrderDetailsDocuments';
import ShipmentDetails from '../../components/ShipmentDetails/ShipmentDetails';
import UserPortalSideBar from '../../containers/UserSideBarContainer/UserSideBarContainer';
import { getOrderById, getOrderStatusHistory, cancelOrder, updateUserOrderItemQuantity, removeUserOrderItem, requestReconfirmation } from '../../api/api';
import {
  PageContainer, BodyWrapper, SidebarPaper,
  ContentContainer, VerticalPaddingContainer,
  SectionCard, ScrollableSection
} from './UserOrderManagement.styles';
import { 
  Box, 
  Stack, 
  Typography, 
  Divider, 
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Tooltip,
  IconButton,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import { useToast } from '../../components/ToastProvider/ToastProvider';

const STATUS = ['AWAITING VENDOR CONFIRMATION', 'VENDOR VALIDATION REQUESTED', 'VENDOR CONFIRMED', 'AWAITING PAYMENT', 'PAID', 'VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED', 'CANCELLED'];

const CANCELLATION_REASONS = [
  'Ordered the wrong item',
  'Found a better price elsewhere',
  'Order placed by mistake',
  'Need to change delivery address',
  'Delivery time is too long',
  'Payment issue or transaction failed',
  'Item no longer needed',
  'Decided to buy a different product',
  'Duplicate order placed'
];

// User portal timeline checkpoints (simplified, user-friendly labels)
const USER_TIMELINE_CHECKPOINTS = [
  { display: 'Awaiting Order Confirmation', statuses: ['AWAITING VENDOR CONFIRMATION', 'VENDOR VALIDATION REQUESTED', 'VENDOR CONFIRMED'] },
  { display: 'Order confirmed', statuses: [] }, // Will be shown when moving to AWAITING PAYMENT
  { display: 'Awaiting Payment', statuses: ['AWAITING PAYMENT'] },
  { display: 'Paid', statuses: ['PAID'] },
  { display: 'Assembling Order', statuses: ['VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER'] },
  { display: 'Shipped', statuses: ['SHIPPED'] },
  { display: 'Delivered', statuses: ['DELIVERED'] },
];

// Base statuses for timeline (mapped to backend statuses)
const BASE_STATUSES = [
  'AWAITING VENDOR CONFIRMATION', // Maps to checkpoint 0: "Awaiting Order Confirmation"
  'VENDOR CONFIRMED', // Maps to checkpoint 1: "Order confirmed" (when reached)
  'AWAITING PAYMENT', // Maps to checkpoint 2: "Awaiting Payment"
  'PAID', // Maps to checkpoint 3: "Paid"
  'ASSEMBLING ORDER', // Maps to checkpoint 4: "Assembling Order" (VENDOR ORDER PROCESSED also maps here)
  'SHIPPED', // Maps to checkpoint 5: "Shipped"
  'DELIVERED', // Maps to checkpoint 6: "Delivered"
];

// Compact payment status chip (doesn't stretch; color-coded)
const PaymentStatusChip = ({ isPaid, statusText }) => {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderRadius: 16,
    fontSize: 14,
    fontWeight: 600,
    width: 'fit-content',
    border: '1px solid transparent',
  };

  // If statusText is null, show "-"
  if (statusText === null) {
    return <span style={{ ...base, color: '#666', border: 'none', padding: '6px 0' }}>—</span>;
  }

  const style = isPaid
    ? { ...base, background: '#E8F5E9', color: '#1B5E20', borderColor: '#A5D6A7' }
    : { ...base, background: '#FFF9C4', color: '#F57F17', borderColor: '#FFEB3B' };

  return <span style={style}>{statusText || (isPaid ? 'Paid' : 'Not Paid')}</span>;
};

/**
 * User Order Management Component
 * 
 * Timeline Sync Logic:
 * - User portal timeline automatically syncs with admin portal status changes
 * - Order status is read from backend API (getOrderById) which reflects admin updates
 * - Timeline checkpoints map backend statuses to user-friendly labels:
 *   1. Order creation: "AWAITING VENDOR CONFIRMATION" -> "Awaiting Order Confirmation"
 *   2. Admin: "VENDOR CONFIRMED" -> "Order confirmed"
 *   3. Admin: "AWAITING PAYMENT" -> "Awaiting Payment"
 *   4. Admin: "PAID" -> "Paid"
 *   5. Admin: "ASSEMBLING ORDER" or "VENDOR ORDER PROCESSED" -> "Assembling Order"
 *   6. Admin: "SHIPPED" -> "Shipped"
 *   7. Admin: "DELIVERED" -> "Delivered"
 *   8. Admin: "CANCELLED" -> "Cancelled" (shows after last checkpoint reached)
 * 
 * The sync happens automatically because:
 * - Backend updates order.status when admin changes it
 * - User portal fetches order.status from API
 * - Timeline displays based on current order.status value
 */
export default function UserOrderManagement() {
  const { showToast } = useToast();
  const [tabIndex, setTabIndex] = useState(0);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prevBeforeCancel, setPrevBeforeCancel] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [customCancelReason, setCustomCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [shippingBreakdown, setShippingBreakdown] = useState(null);
  const [localItemChanges, setLocalItemChanges] = useState({}); // Track local quantity changes
  const [removedItems, setRemovedItems] = useState(new Set()); // Track locally removed items
  const [requestingReconfirmation, setRequestingReconfirmation] = useState(false);
  const [shipmentCount, setShipmentCount] = useState(null);
  const params = useParams();

  // Get current order status (defaults to "AWAITING VENDOR CONFIRMATION" on order creation)
  const currentStatus = order?.status && STATUS.includes(order.status) ? order.status : 'AWAITING VENDOR CONFIRMATION';
  
  // Determine payment status based on order status
  // - Before AWAITING PAYMENT: show "-" (not applicable)
  // - AWAITING PAYMENT: show "Awaiting Payment"
  // - PAID and after: show "Paid"
  const isPaid = currentStatus === 'PAID' || 
                 ['VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED'].includes(currentStatus);
  
  // Payment status display logic
  const getPaymentStatusDisplay = () => {
    const beforeAwaitingPayment = [
      'AWAITING VENDOR CONFIRMATION',
      'VENDOR VALIDATION REQUESTED',
      'VENDOR CONFIRMED'
    ];
    
    if (beforeAwaitingPayment.includes(currentStatus) || currentStatus === 'CANCELLED') {
      return null; // Will show "-" in PaymentStatusChip
    }
    
    if (currentStatus === 'AWAITING PAYMENT') {
      return 'Awaiting Payment';
    }
    
    if (isPaid) {
      return 'Paid';
    }
    
    return null; // Default fallback
  };

  // Check if order is cancelled
  const isCancelled = currentStatus === 'CANCELLED';

  // Check if cancel order button should be visible (for AWAITING PAYMENT and all statuses before it)
  // Button should be hidden when status is PAID or any status after PAID
  const showCancelOrderButton = useMemo(() => {
    if (isCancelled || !order?.id) return false;
    const paidIdx = STATUS.indexOf('PAID');
    const currentStatusIdx = STATUS.indexOf(currentStatus);
    // Show button if current status is before PAID (or if PAID is not found, show for all)
    return paidIdx >= 0 && currentStatusIdx >= 0 && currentStatusIdx < paidIdx;
  }, [isCancelled, order?.id, currentStatus]);

  // Check if a valid cancellation reason is provided
  const hasValidReason = useMemo(() => {
    return cancelReason.trim() !== '' || customCancelReason.trim() !== '';
  }, [cancelReason, customCancelReason]);

  // Check if order is in AWAITING PAYMENT status (allows user modifications)
  const canModifyOrder = useMemo(() => {
    return currentStatus === 'AWAITING PAYMENT' && !isCancelled;
  }, [currentStatus, isCancelled]);

  // Check if there are any local changes (quantity updates or removals)
  const hasLocalChanges = useMemo(() => {
    return Object.keys(localItemChanges).length > 0 || removedItems.size > 0;
  }, [localItemChanges, removedItems]);

  // Handle quantity update (local only, not sent to API until Request Re-Confirmation)
  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      showToast('Quantity must be at least 1', 'error');
      return;
    }
    setLocalItemChanges(prev => ({ ...prev, [itemId]: newQuantity }));
  };

  // Handle item removal (local only, not sent to API until Request Re-Confirmation)
  const handleRemoveItem = (itemId) => {
    const remainingItems = (order?.items || []).filter(item => 
      item.id !== itemId && !removedItems.has(item.id)
    );
    if (remainingItems.length === 0) {
      showToast('Cannot remove the last item. Please cancel the order instead.', 'error');
      return;
    }
    setRemovedItems(prev => new Set([...prev, itemId]));
    // Remove from localItemChanges if present
    setLocalItemChanges(prev => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
    showToast('Item marked for removal. Click "Request Re-Confirmation" to apply changes.', 'info');
  };

  // Handle undo removal (restore item)
  const handleUndoRemove = (itemId) => {
    setRemovedItems(prev => {
      const updated = new Set(prev);
      updated.delete(itemId);
      return updated;
    });
    showToast('Item restored.', 'success');
  };

  // Handle Request Re-Confirmation
  const handleRequestReconfirmation = async () => {
    if (!order?.id) return;
    
    try {
      setRequestingReconfirmation(true);
      
      // First, apply local quantity changes
      for (const [itemId, quantity] of Object.entries(localItemChanges)) {
        try {
          await updateUserOrderItemQuantity(order.id, itemId, quantity);
        } catch (error) {
          console.error(`Failed to update quantity for item ${itemId}:`, error);
          showToast(`Failed to update quantity for item ${itemId}`, 'error');
          setRequestingReconfirmation(false);
          return;
        }
      }
      
      // Then, remove items
      for (const itemId of removedItems) {
        try {
          await removeUserOrderItem(order.id, itemId);
        } catch (error) {
          console.error(`Failed to remove item ${itemId}:`, error);
          showToast(`Failed to remove item ${itemId}`, 'error');
          setRequestingReconfirmation(false);
          return;
        }
      }
      
      // Finally, request re-confirmation
      await requestReconfirmation(order.id);
      
      showToast('Re-confirmation requested successfully. Order status has been reset to "Awaiting Vendor Confirmation".', 'success');
      
      // Reset local changes
      setLocalItemChanges({});
      setRemovedItems(new Set());
      
      // Refresh order data
      const orderData = await getOrderById(order.id);
      setOrder(orderData);
    } catch (error) {
      console.error('Failed to request re-confirmation:', error);
      showToast(`Failed to request re-confirmation: ${error?.message || error}`, 'error');
    } finally {
      setRequestingReconfirmation(false);
    }
  };

  useEffect(() => {
    const fetchOrder = async () => {
      if (params.orderId) {
        try {
          setLoading(true);
          const orderData = await getOrderById(params.orderId);
          setOrder(orderData);
        } catch (err) {
          console.error('Error fetching order:', err);
          setError(err.message || 'Failed to load order');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchOrder();
  }, [params.orderId]);

  // User portal: No shipping calculation - just show what admin validated
  // Shipping breakdown is not needed for user portal
  useEffect(() => {
    // Clear shipping breakdown - user portal doesn't calculate shipping
    setShippingBreakdown(null);
  }, [order?.id]);

  // When cancelled, try to fetch last previous status from status-history to shape the timeline
  // Note: This may fail if user doesn't have admin access, so we handle errors gracefully
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (currentStatus !== 'CANCELLED' || !params.orderId) {
          if (alive) setPrevBeforeCancel(null);
          return;
        }
        // Try to fetch history, but don't fail if user lacks permission
        try {
          const history = await getOrderStatusHistory(params.orderId);
          if (!alive || !Array.isArray(history) || !history.length) return;
          const lastCancel = history.findLast?.((h) => h.new_status === 'CANCELLED') || [...history].reverse().find((h) => h.new_status === 'CANCELLED');
          // Check if old_status is valid (any status from STATUS array)
          if (lastCancel?.old_status && STATUS.includes(lastCancel.old_status)) {
            if (alive) setPrevBeforeCancel(lastCancel.old_status);
          } else {
            if (alive) setPrevBeforeCancel(null);
          }
        } catch (err) {
          // User may not have admin access - that's OK, just use default timeline
          if (alive) setPrevBeforeCancel(null);
        }
      } catch {
        if (alive) setPrevBeforeCancel(null);
      }
    })();
    return () => { alive = false; };
  }, [currentStatus, params.orderId]);

  // Map backend status to timeline checkpoint index
  // This ensures user portal timeline syncs with admin portal status changes
  const getCheckpointIndex = (status) => {
    const statusUpper = status?.toUpperCase();
    
    // Map backend statuses to checkpoint indices (sync with admin portal)
    // 1. Order creation: "AWAITING VENDOR CONFIRMATION" -> checkpoint 0
    if (['AWAITING VENDOR CONFIRMATION', 'VENDOR VALIDATION REQUESTED'].includes(statusUpper)) {
      return 0; // "Awaiting Order Confirmation"
    }
    // 2. Admin moves to "VENDOR CONFIRMED" -> checkpoint 1
    if (statusUpper === 'VENDOR CONFIRMED') {
      return 1; // "Order confirmed"
    }
    // 3. Admin moves to "AWAITING PAYMENT" -> checkpoint 2
    if (statusUpper === 'AWAITING PAYMENT') {
      return 2; // "Awaiting Payment"
    }
    // 4. Admin moves to "PAID" -> checkpoint 3
    if (statusUpper === 'PAID') {
      return 3; // "Paid"
    }
    // 5. Admin moves to "ASSEMBLING ORDER" or "VENDOR ORDER PROCESSED" -> checkpoint 4
    if (['VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER'].includes(statusUpper)) {
      return 4; // "Assembling Order"
    }
    // 6. Admin moves to "SHIPPED" -> checkpoint 5
    if (statusUpper === 'SHIPPED') {
      return 5; // "Shipped"
    }
    // 7. Admin moves to "DELIVERED" -> checkpoint 6
    if (statusUpper === 'DELIVERED') {
      return 6; // "Delivered"
    }
    // 8. Admin cancels order: "CANCELLED" -> special handling
    if (statusUpper === 'CANCELLED') {
      return -1; // Special marker for cancelled (handled separately)
    }
    return 0; // Default to first checkpoint
  };

  // Aesthetic timeline bar with completed ticks
  function TimelineBar() {
    const siteBlue = '#002F87';
    const inactive = '#c7cfdd';
    
    // Get user-friendly checkpoint labels
    const checkpointLabels = USER_TIMELINE_CHECKPOINTS.map(cp => cp.display);
    
    // Determine current checkpoint index
    const currentCheckpointIdx = getCheckpointIndex(currentStatus);
    
    // Build timeline statuses (for cancelled orders, show up to cancellation point)
    const timelineStatuses = useMemo(() => {
      if (currentStatus === 'CANCELLED') {
        // For cancelled orders, show timeline up to the point before cancellation
        const cancelledCheckpointIdx = prevBeforeCancel ? getCheckpointIndex(prevBeforeCancel) : -1;
        const maxIdx = cancelledCheckpointIdx >= 0 ? cancelledCheckpointIdx : checkpointLabels.length - 1;
        // Show cancelled status after the last checkpoint reached
        const baseTimeline = checkpointLabels.slice(0, maxIdx + 1);
        return [...baseTimeline, 'Cancelled'];
      }
      return checkpointLabels;
    }, [currentStatus, prevBeforeCancel, checkpointLabels]);
    
    // For cancelled orders, current index should be the "Cancelled" checkpoint
    const effectiveCurrentIdx = currentStatus === 'CANCELLED' 
      ? timelineStatuses.length - 1 
      : currentCheckpointIdx;

    return (
      <Box sx={{ px: 2, pt: 3, pb: 3 }}>
        <Stack direction="row" alignItems="flex-start" spacing={0} sx={{ width: '100%', position: 'relative' }}>
          {timelineStatuses.map((label, idx) => {
            const isCompleted = idx < effectiveCurrentIdx;
            const isCurrent = idx === effectiveCurrentIdx;
            const isCancelled = label === 'Cancelled';
            const dotSize = 20;

            return (
              <React.Fragment key={`timeline-${label}`}>
                <Stack 
                  alignItems="center" 
                  sx={{ 
                    minWidth: { xs: 90, sm: 110 },
                    flex: 1,
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 2,
                      color: isCancelled ? '#dc3545' : (isCompleted || isCurrent ? siteBlue : '#4b5563'),
                      fontWeight: 600,
                      textAlign: 'center',
                      letterSpacing: 0.15,
                      fontSize: '0.7rem',
                      lineHeight: 1.3,
                      minHeight: '3.6em',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.5,
                      wordBreak: 'break-word',
                    }}
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      width: dotSize,
                      height: dotSize,
                      borderRadius: '50%',
                      border: `3px solid ${isCancelled ? '#dc3545' : (isCompleted || isCurrent ? siteBlue : inactive)}`,
                      backgroundColor: isCancelled ? '#dc3545' : (isCompleted ? siteBlue : '#fff'),
                      transition: 'all 300ms ease',
                      boxShadow: isCancelled || isCurrent || isCompleted 
                        ? `0 0 0 4px ${isCancelled ? '#dc354520' : `${siteBlue}20`}` 
                        : 'none',
                      flexShrink: 0,
                    }}
                  >
                    {isCompleted && (
                      <CheckRoundedIcon
                        sx={{
                          color: '#fff',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '0.875rem',
                        }}
                      />
                    )}
                    {isCurrent && !isCompleted && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: 10,
                          height: 10,
                          bgcolor: siteBlue,
                          borderRadius: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    )}
                  </Box>
                </Stack>
                {idx < timelineStatuses.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: `calc(3.6em + ${dotSize / 2}px)`,
                      transform: 'translateY(-50%)',
                      left: `calc(${(idx + 1) * (100 / timelineStatuses.length)}% - ${dotSize / 2}px)`,
                      right: `calc(${(timelineStatuses.length - idx - 1) * (100 / timelineStatuses.length)}% - ${dotSize / 2}px)`,
                      height: 2,
                      borderTop: isCompleted
                        ? `2px solid #9ca3af`
                        : `2px dotted ${inactive}`,
                      borderRadius: 0,
                      transition: 'border-color 300ms ease',
                      zIndex: 1,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      </Box>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <TopNavbar onTabChange={setTabIndex} />
        <BodyWrapper>
          <SidebarPaper>
            <UserPortalSideBar />
          </SidebarPaper>
          <ContentContainer>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <div>Loading order details...</div>
            </div>
          </ContentContainer>
        </BodyWrapper>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <TopNavbar onTabChange={setTabIndex} />
        <BodyWrapper>
          <SidebarPaper>
            <UserPortalSideBar />
          </SidebarPaper>
          <ContentContainer>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <div style={{ color: 'red' }}>Error: {error}</div>
            </div>
          </ContentContainer>
        </BodyWrapper>
      </PageContainer>
    );
  }

  // Handle cancel order
  const handleCancelOrder = async () => {
    const finalReason = cancelReason || customCancelReason.trim();
    if (!order?.id || !finalReason || cancelling) return;
    try {
      setCancelling(true);
      const updated = await cancelOrder(order.id, finalReason);
      setOrder(updated);
      setCancelDialogOpen(false);
      setCancelReason('');
      setCustomCancelReason('');
      showToast('Order cancelled successfully', 'success');
      // Reload order to get updated status
      if (params.orderId) {
        const refreshedOrder = await getOrderById(params.orderId);
        setOrder(refreshedOrder);
      }
    } catch (e) {
      console.error('Cancel order failed', e);
      showToast(`Failed to cancel order: ${e?.message || e}`, 'error');
    } finally {
      setCancelling(false);
    }
  };

  // Handle radio selection - clear custom text when radio is selected
  const handleRadioChange = (e) => {
    setCancelReason(e.target.value);
    setCustomCancelReason(''); // Clear custom text when radio is selected
  };

  // Handle custom text change - clear radio when custom text is entered
  const handleCustomTextChange = (e) => {
    setCustomCancelReason(e.target.value);
    if (e.target.value.trim() !== '') {
      setCancelReason(''); // Clear radio selection when custom text is entered
    }
  };

  // Handle custom text focus - clear radio on focus
  const handleCustomTextFocus = () => {
    setCancelReason('');
  };

  return (
    <PageContainer>
      <TopNavbar onTabChange={setTabIndex} />

      <BodyWrapper>
        <SidebarPaper>
          <UserPortalSideBar />
        </SidebarPaper>

        <ContentContainer>
          <ScrollableSection>
            <VerticalPaddingContainer>
              {/* Order Number Header */}
              {order?.order_number && (
                <Box sx={{ mb: 2, px: 2, pt: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#002F87',
                      fontSize: '1rem'
                    }}
                  >
                    Order #{order.order_number}
                  </Typography>
                </Box>
              )}
              {/* Timeline Bar */}
              <TimelineBar />

              {/* Cancel Order Button - Only show for AWAITING PAYMENT status */}
              {showCancelOrderButton && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, mt: 2, px: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="medium"
                    startIcon={<CancelIcon />}
                    onClick={() => setCancelDialogOpen(true)}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    Cancel Order
                  </Button>
                </Box>
              )}

              {/* Product confirmation table */}
              <SectionCard>
                {canModifyOrder && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Order Modifications (AWAITING PAYMENT)
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                      You can update quantities or remove items. Click "Request Re-Confirmation" to apply changes.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!hasLocalChanges || requestingReconfirmation}
                      onClick={handleRequestReconfirmation}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {requestingReconfirmation ? 'Requesting...' : 'Request Re-Confirmation'}
                    </Button>
                  </Box>
                )}
                <ProductConfirmation 
                  order={order} 
                  showSystemNote={false} 
                  editableActions={canModifyOrder}
                  isAdmin={false}
                  allowUserModifications={canModifyOrder}
                  localItemChanges={localItemChanges}
                  removedItems={removedItems}
                  onQuantityChange={handleQuantityChange}
                  onRemoveItem={handleRemoveItem}
                  onUndoRemove={handleUndoRemove}
                />
              </SectionCard>

              {/* Shipment Details Section */}
              {order?.id && (
                <SectionCard>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Shipment Details
                    {shipmentCount !== null && shipmentCount > 0 && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 'normal', fontStyle: 'italic' }}>
                        {shipmentCount > 1 
                          ? `(Order will be delivered in ${shipmentCount} shipments)`
                          : '(Order will be shipped in a single shipment.)'
                        }
                      </Typography>
                    )}
                  </Typography>
                  <ShipmentDetails 
                    orderId={order.id} 
                    orderItems={order.items} 
                    isAdmin={false}
                    onShipmentCountChange={setShipmentCount}
                  />
                </SectionCard>
              )}

              {/* Address Details & Order Totals */}
              <SectionCard>
                <Box sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
                    {/* Left Column - Shipping Address */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                        Shipping Address
                      </Typography>
                      <Box sx={{ color: '#666', lineHeight: 1.8 }}>
                        {order?.shipping_name && (
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {order.shipping_name}
                          </Typography>
                        )}
                        {order?.shipping_phone && (
                          <Typography variant="body2">{order.shipping_phone}</Typography>
                        )}
                        {order?.shipping_address_1 && (
                          <Typography variant="body2">{order.shipping_address_1}</Typography>
                        )}
                        {order?.shipping_address_2 && (
                          <Typography variant="body2">{order.shipping_address_2}</Typography>
                        )}
                        {[order?.shipping_city, order?.shipping_state, order?.shipping_postal_code].filter(Boolean).length > 0 && (
                          <Typography variant="body2">
                            {[order?.shipping_city, order?.shipping_state, order?.shipping_postal_code].filter(Boolean).join(', ')}
                          </Typography>
                        )}
                        {order?.shipping_country && (
                          <Typography variant="body2">{order.shipping_country}</Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Right Column - Order Totals */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                      <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: '300px' } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, textAlign: { xs: 'left', md: 'right' } }}>
                          Order Summary
                        </Typography>
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>Subtotal:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {(() => {
                                // Use order.subtotal directly from backend (same as email template)
                                const isOrderValidated = currentStatus === 'AWAITING PAYMENT' || 
                                                         currentStatus === 'PAID' || 
                                                         currentStatus === 'VENDOR ORDER PROCESSED' || 
                                                         currentStatus === 'ASSEMBLING ORDER' || 
                                                         currentStatus === 'SHIPPED' || 
                                                         currentStatus === 'DELIVERED';
                                
                                // If order is validated, always show subtotal from backend (same as email template)
                                if (isOrderValidated) {
                                  return `$${(order?.subtotal || 0).toFixed(2)}`;
                                }
                                
                                // If not validated, check for ON_DEMAND items
                                const hasOnDemandItems = order?.items?.some(item => {
                                  const unitPriceNum = Number(item.unit_price ?? item.product_price ?? 0);
                                  return (item.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
                                         (item.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
                                });
                                
                                // If not validated and has ON_DEMAND items, show "To be calculated"
                                return hasOnDemandItems ? "To be calculated" : `$${(order?.subtotal || 0).toFixed(2)}`;
                              })()}
                            </Typography>
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#666' }}>Shipping:</Typography>
                                {/* User portal: Show validated shipping value in tooltip */}
                                <Tooltip
                                  title={
                                    <Box sx={{ 
                                      maxWidth: 400, 
                                      p: 2, 
                                      backgroundColor: 'white', 
                                      borderRadius: 2,
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                      border: '1px solid #e0e0e0'
                                    }}>
                                      {(() => {
                                        // User portal: Show validated shipping value from order
                                        const isOrderValidated = currentStatus === 'AWAITING PAYMENT' || 
                                                                 currentStatus === 'PAID' || 
                                                                 currentStatus === 'VENDOR ORDER PROCESSED' || 
                                                                 currentStatus === 'ASSEMBLING ORDER' || 
                                                                 currentStatus === 'SHIPPED' || 
                                                                 currentStatus === 'DELIVERED';
                                        
                                        const validatedShipping = order?.shipping_cost || 0;
                                        
                                        if (isOrderValidated) {
                                          return (
                                            <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                                              Validated Shipping Cost: ${validatedShipping.toFixed(2)}
                                            </Typography>
                                          );
                                        } else {
                                          return (
                                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                                              Shipping will be calculated once order is confirmed
                                            </Typography>
                                          );
                                        }
                                      })()}
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <IconButton size="small" sx={{ p: 0.5 }}>
                                    <InfoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {/* Old breakdown code removed - user portal doesn't calculate shipping */}
                                {false && shippingBreakdown?.breakdown?.length > 0 && (
                                  <Tooltip
                                    title={
                                      <Box sx={{ 
                                        maxWidth: 400, 
                                        p: 2, 
                                        backgroundColor: 'white', 
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        border: '1px solid #e0e0e0'
                                      }}>
                                        {shippingBreakdown.has_manual_quotes && shippingBreakdown.total_shipping_cost === 0 ? (
                                          <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                                            Shipping will be calculated once order is confirmed
                                          </Typography>
                                        ) : (() => {
                                          // Debug: Log breakdown structure
                                          console.log('=== SHIPPING BREAKDOWN DEBUG ===');
                                          console.log('Full breakdown:', JSON.stringify(shippingBreakdown, null, 2));
                                          console.log('Breakdown items:', shippingBreakdown.breakdown?.map(bd => ({
                                            vendor: bd.vendor,
                                            category_group: bd.category_group,
                                            cost: bd.cost,
                                            message: bd.message,
                                            total_quantity: bd.total_quantity
                                          })));
                                          console.log('Order items:', order?.items?.map(item => ({
                                            id: item.id,
                                            description: item.snapshot_product_description,
                                            vendor: item.product?.vendor || item.snapshot_product_vendor,
                                            category: item.product?.category || item.snapshot_product_category,
                                            availability: item.availability
                                          })));
                                          
                                          // Calculate remaining shipping for "to be calculated" items
                                          // When order is validated, use order.shipping_cost (from backend after validation)
                                          // Otherwise use breakdown total
                                          const isOrderValidated = currentStatus === 'AWAITING PAYMENT' || 
                                                                   currentStatus === 'PAID' || 
                                                                   currentStatus === 'VENDOR ORDER PROCESSED' || 
                                                                   currentStatus === 'ASSEMBLING ORDER' || 
                                                                   currentStatus === 'SHIPPED' || 
                                                                   currentStatus === 'DELIVERED';
                                          
                                          const finalShipping = isOrderValidated && order?.shipping_cost 
                                            ? order.shipping_cost 
                                            : (shippingBreakdown.total_shipping_cost || 0);
                                          
                                          const calculatedShipping = shippingBreakdown.breakdown
                                            .filter(item => !item.message?.includes('To be calculated') && item.cost > 0)
                                            .reduce((sum, item) => sum + (item.cost || 0), 0);
                                          const remainingShipping = finalShipping - calculatedShipping;
                                          const toBeCalculatedGroups = shippingBreakdown.breakdown.filter(
                                            item => item.message?.includes('To be calculated')
                                          );
                                          const remainingPerGroup = toBeCalculatedGroups.length > 0 
                                            ? remainingShipping / toBeCalculatedGroups.length 
                                            : 0;
                                          
                                          console.log('Calculated shipping sum:', calculatedShipping);
                                          console.log('Final shipping:', finalShipping);
                                          console.log('Remaining shipping:', remainingShipping);
                                          console.log('To be calculated groups:', toBeCalculatedGroups.length);
                                          console.log('Remaining per group:', remainingPerGroup);
                                          
                                          // Group order items by their shipping breakdown group
                                          // Note: Backend only includes available items in breakdown, so we should filter too
                                          const availableOrderItems = (order?.items || []).filter(item => 
                                            item.availability === 'available' || !item.availability
                                          );
                                          
                                          const orderItemsWithShipping = availableOrderItems.map(orderItem => {
                                            // Find matching shipping breakdown group
                                            // Try multiple sources for vendor and category
                                            const vendor = orderItem.product?.vendor || 
                                                          orderItem.snapshot_product_vendor || 
                                                          orderItem.product?.brand ||
                                                          orderItem.snapshot_product_brand;
                                            const category = orderItem.product?.category || 
                                                            orderItem.snapshot_product_category;
                                            
                                            const orderItemDescription = orderItem.snapshot_product_description || 
                                                                         orderItem.product_name || 
                                                                         orderItem.product?.description || 
                                                                         '';
                                            
                                            // Normalize vendor name (EXACT same logic as backend ShippingCalculator._normalize_vendor_name)
                                            const normalizeVendor = (v) => {
                                              if (!v) return v;
                                              
                                              const vLower = v.toLowerCase().trim();
                                              
                                              // Backend vendor mappings
                                              const vendorMappings = {
                                                'mfp-technology': 'MFP Technology',
                                                'mfp_technology': 'MFP Technology',
                                                'mfp technology': 'MFP Technology',
                                                'sourcing-it': 'Sourcing IT',
                                                'sourcing_it': 'Sourcing IT',
                                                'sourcing it': 'Sourcing IT',
                                                'advent-data-solution': 'Advent Data Solution',
                                                'advent_data_solution': 'Advent Data Solution',
                                                'advent data solution': 'Advent Data Solution',
                                                'sla-expert': 'SLA Expert',
                                                'sla_expert': 'SLA Expert',
                                                'sla expert': 'SLA Expert'
                                              };
                                              
                                              // Direct mapping
                                              if (vLower in vendorMappings) {
                                                return vendorMappings[vLower];
                                              }
                                              
                                              // If no mapping found, return original vendor (same as backend)
                                              return v;
                                            };
                                            
                                            // Map category to group (EXACT same logic as backend ShippingCalculator._map_category_to_group)
                                            const mapCategoryToGroup = (cat) => {
                                              if (!cat) return 'Small Components';  // Backend default
                                              
                                              const catLower = cat.toLowerCase().trim();
                                              
                                              // Backend CATEGORY_MAPPING
                                              const categoryMapping = {
                                                'adapter': 'Small Components',
                                                'raid': 'Small Components',
                                                'hba': 'Small Components',
                                                'ssd': 'Small Components',
                                                'hdd': 'Small Components',
                                                'hard drive': 'Small Components',
                                                'fans': 'Small Components',
                                                'processor': 'Small Components',
                                                'sfp': 'Small Components',
                                                'cables': 'Small Components',
                                                'memory': 'Small Components',
                                                'heatsinks': 'Small Components',
                                                'power supplies': 'Small Components',
                                                'power supply': 'Small Components',
                                                'mice': 'Small Components',
                                                'risers': 'Small Components',
                                                'gpu': 'Small Components',
                                                'network adapter': 'Small Components',
                                                'server': 'Large Equipment',
                                                'servers': 'Large Equipment',
                                                'storage': 'Large Equipment',
                                                'monitor': 'Large Equipment',
                                                'monitors': 'Large Equipment',
                                                'tape enclosure': 'Large Equipment',
                                                'tape enclosures': 'Large Equipment',
                                                'motherboard': 'Medium Components',
                                                'motherboards': 'Medium Components',
                                                'expansion board': 'Medium Components',
                                                'expansion boards': 'Medium Components',
                                                'tape drive': 'Medium Components',
                                                'tape drives': 'Medium Components',
                                                'switch': 'Switches',
                                                'switches': 'Switches',
                                                'keyboard': 'Keyboards',
                                                'keyboards': 'Keyboards',
                                                'pdu': 'PDUs',
                                                'pdus': 'PDUs',
                                                'rail kit': 'Rail Kits',
                                                'rack kit': 'Rail Kits'
                                              };
                                              
                                              // Direct mapping
                                              if (catLower in categoryMapping) {
                                                return categoryMapping[catLower];
                                              }
                                              
                                              // Partial matching (same as backend)
                                              for (const [key, group] of Object.entries(categoryMapping)) {
                                                if (catLower.includes(key)) {
                                                  return group;
                                                }
                                              }
                                              
                                              // Default fallback (same as backend)
                                              return 'Small Components';
                                            };
                                            
                                            const normalizedVendor = normalizeVendor(vendor);
                                            const categoryGroup = mapCategoryToGroup(category);
                                            
                                            // Debug: Log what we're looking for
                                            console.log('Matching item:', {
                                              product: orderItem.snapshot_product_description,
                                              vendor: vendor,
                                              normalizedVendor: normalizedVendor,
                                              category: category,
                                              categoryGroup: categoryGroup
                                            });
                                            
                                            // First try: Match by vendor + category
                                            let matchingBreakdown = shippingBreakdown.breakdown.find(bd => {
                                              const bdVendor = (bd.vendor || '').toLowerCase().trim();
                                              const bdCategory = (bd.category_group || '').toLowerCase().trim();
                                              const orderVendor = (normalizedVendor || '').toLowerCase().trim();
                                              const orderCategory = (categoryGroup || '').toLowerCase().trim();
                                              
                                              return bdVendor === orderVendor && bdCategory === orderCategory;
                                            });
                                            
                                            // Fallback: If vendor is missing, match by description from breakdown items
                                            if (!matchingBreakdown && orderItemDescription) {
                                              matchingBreakdown = shippingBreakdown.breakdown.find(bd => {
                                                if (!bd.items || !Array.isArray(bd.items)) return false;
                                                return bd.items.some(bdItem => {
                                                  const bdDescription = (bdItem.description || '').toLowerCase().trim();
                                                  const orderDesc = orderItemDescription.toLowerCase().trim();
                                                  // Check if descriptions match (exact or partial)
                                                  return bdDescription === orderDesc || 
                                                         bdDescription.includes(orderDesc) || 
                                                         orderDesc.includes(bdDescription);
                                                });
                                              });
                                              
                                              if (matchingBreakdown) {
                                                console.log('  ✅ Match found by description fallback:', {
                                                  orderDescription: orderItemDescription,
                                                  breakdownVendor: matchingBreakdown.vendor,
                                                  breakdownCategory: matchingBreakdown.category_group
                                                });
                                              }
                                            }
                                            
                                            if (!matchingBreakdown) {
                                              console.warn('  ❌ No breakdown match found. Available breakdowns:', 
                                                shippingBreakdown.breakdown.map(bd => ({
                                                  vendor: bd.vendor,
                                                  category_group: bd.category_group,
                                                  cost: bd.cost,
                                                  message: bd.message,
                                                  items: bd.items?.map(i => i.description)
                                                }))
                                              );
                                            } else {
                                              console.log('  ✅ Match found:', {
                                                vendor: matchingBreakdown.vendor,
                                                category_group: matchingBreakdown.category_group,
                                                cost: matchingBreakdown.cost,
                                                message: matchingBreakdown.message
                                              });
                                            }
                                            
                                            return {
                                              orderItem,
                                              breakdown: matchingBreakdown,
                                              productName: orderItem.snapshot_product_description || 
                                                          orderItem.product_name || 
                                                          orderItem.product?.description || 
                                                          'Unknown Product'
                                            };
                                          });
                                          
                                          // Group by shipping breakdown to avoid duplicates
                                          const groupedByShipping = {};
                                          orderItemsWithShipping.forEach(({ orderItem, breakdown, productName }) => {
                                            // Debug logging
                                            if (!breakdown) {
                                              console.warn('No breakdown match for item:', {
                                                product: productName,
                                                vendor: orderItem.product?.vendor || orderItem.snapshot_product_vendor,
                                                category: orderItem.product?.category || orderItem.snapshot_product_category,
                                                availableBreakdowns: shippingBreakdown.breakdown.map(bd => ({
                                                  vendor: bd.vendor,
                                                  category_group: bd.category_group,
                                                  cost: bd.cost,
                                                  message: bd.message
                                                }))
                                              });
                                            }
                                            
                                            const key = breakdown ? `${breakdown.vendor}_${breakdown.category_group}` : `unknown_${orderItem.id}`;
                                            if (!groupedByShipping[key]) {
                                              groupedByShipping[key] = {
                                                breakdown,
                                                items: [],
                                                totalQuantity: 0
                                              };
                                            }
                                            groupedByShipping[key].items.push({ orderItem, productName });
                                            groupedByShipping[key].totalQuantity += orderItem.quantity || 1;
                                          });
                                          
                                          return (
                                            <>
                                              {/* Total Items Header */}
                                              <Typography variant="h6" sx={{ 
                                                mb: 2, 
                                                fontWeight: 'bold', 
                                                color: '#333',
                                                borderBottom: '1px solid #e0e0e0',
                                                pb: 1
                                              }}>
                                                Total Items: {order?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                                              </Typography>
                                              
                                              {Object.values(groupedByShipping).map((group, index) => {
                                                // Check if breakdown exists
                                                if (!group.breakdown) {
                                                  return (
                                                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < Object.keys(groupedByShipping).length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                                      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem', lineHeight: 1.4, whiteSpace: 'pre-line', color: '#555' }}>
                                                        {group.items.map((item, idx) => 
                                                          `${idx + 1}. ${item.productName} (${item.orderItem.quantity || 1})`
                                                        ).join('\n')}
                                                      </Typography>
                                                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <span>Shipping:</span>
                                                        <span>To be calculated</span>
                                                      </Typography>
                                                    </Box>
                                                  );
                                                }
                                                
                                                const breakdown = group.breakdown;
                                                
                                                // If no breakdown found, show "To be calculated"
                                                if (!breakdown) {
                                                  console.warn('  ⚠️ No breakdown for group:', group.items.map(i => i.productName).join(', '));
                                                  return (
                                                    <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < Object.keys(groupedByShipping).length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                                        {group.items.map(i => i.productName).join(', ')}
                                                      </Typography>
                                                      <Typography variant="body2" sx={{ color: '#666' }}>
                                                        Shipping: To be calculated
                                                      </Typography>
                                                    </Box>
                                                  );
                                                }
                                                
                                                // ONLY check message for "To be calculated" - this is the reliable indicator
                                                const isToBeCalculated = breakdown.message?.includes('To be calculated') || false;
                                                
                                                // If cost > 0, it's calculated shipping (from matrix or DHL API)
                                                const hasCalculatedShipping = breakdown.cost > 0;
                                                
                                                const isOrderValidated = currentStatus === 'AWAITING PAYMENT' || 
                                                                         currentStatus === 'PAID' || 
                                                                         currentStatus === 'VENDOR ORDER PROCESSED' || 
                                                                         currentStatus === 'ASSEMBLING ORDER' || 
                                                                         currentStatus === 'SHIPPED' || 
                                                                         currentStatus === 'DELIVERED';
                                                
                                                let shippingDisplay = 'To be calculated';
                                                
                                                // Debug logging
                                                console.log('Display logic for:', group.items.map(i => i.productName).join(', '), {
                                                  breakdownCost: breakdown.cost,
                                                  breakdownMessage: breakdown.message,
                                                  hasCalculatedShipping,
                                                  isToBeCalculated,
                                                  isOrderValidated,
                                                  finalShipping,
                                                  remainingPerGroup
                                                });
                                                
                                                // If item has calculated shipping (cost > 0), always show actual value
                                                if (hasCalculatedShipping) {
                                                  // Backend sets message to "$XX.XX" for DHL API, or null for matrix
                                                  // If message exists and is a dollar amount, use it; otherwise format cost
                                                  if (breakdown.message && breakdown.message.trim() && !breakdown.message.includes('To be calculated')) {
                                                    shippingDisplay = breakdown.message;
                                                  } else {
                                                    shippingDisplay = `$${Number(breakdown.cost || 0).toFixed(2)}`;
                                                  }
                                                  console.log('  ✅ Showing calculated shipping:', shippingDisplay, '(cost:', breakdown.cost, ', message:', breakdown.message, ')');
                                                }
                                                // If item is "to be calculated" (message says so) and order is validated, show remaining shipping
                                                else if (isToBeCalculated && isOrderValidated && finalShipping > 0 && remainingPerGroup > 0) {
                                                  shippingDisplay = `$${remainingPerGroup.toFixed(2)}`;
                                                  console.log('  ✅ Showing remaining shipping:', shippingDisplay);
                                                }
                                                // Otherwise show "To be calculated"
                                                else if (isToBeCalculated) {
                                                  shippingDisplay = 'To be calculated';
                                                  console.log('  ✅ Showing "To be calculated"');
                                                } else {
                                                  console.warn('  ⚠️ No condition matched! This should not happen. Breakdown:', breakdown);
                                                  // Fallback: if cost exists but no message, show cost
                                                  if (breakdown.cost > 0) {
                                                    shippingDisplay = `$${Number(breakdown.cost || 0).toFixed(2)}`;
                                                    console.log('  ✅ Fallback: Showing cost:', shippingDisplay);
                                                  }
                                                }
                                                
                                                return (
                                                  <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < Object.keys(groupedByShipping).length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                                    {/* Items List */}
                                                    <Typography variant="body2" sx={{ 
                                                      mb: 1, 
                                                      fontSize: '0.9rem', 
                                                      lineHeight: 1.4, 
                                                      whiteSpace: 'pre-line',
                                                      color: '#555'
                                                    }}>
                                                      {group.items.map((item, idx) => 
                                                        `${idx + 1}. ${item.productName} (${item.orderItem.quantity || 1})`
                                                      ).join('\n')}
                                                    </Typography>
                                                    
                                                    {/* Shipping Cost */}
                                                    <Typography variant="body2" sx={{ 
                                                      fontWeight: 'bold', 
                                                      color: isToBeCalculated && !isOrderValidated ? '#ff9800' : '#333',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 1
                                                    }}>
                                                      <span>Shipping:</span>
                                                      <span>{shippingDisplay}</span>
                                                    </Typography>
                                                  </Box>
                                                );
                                              })}
                                              
                                              {/* Show overall asterisk message if any manual quotes and not yet validated */}
                                              {shippingBreakdown.has_manual_quotes && 
                                               currentStatus !== 'AWAITING PAYMENT' && 
                                               currentStatus !== 'PAID' && 
                                               currentStatus !== 'VENDOR ORDER PROCESSED' && 
                                               currentStatus !== 'ASSEMBLING ORDER' && 
                                               currentStatus !== 'SHIPPED' && 
                                               currentStatus !== 'DELIVERED' && (
                                                <Typography variant="caption" sx={{ 
                                                  color: '#ff9800', 
                                                  fontSize: '0.75rem',
                                                  fontStyle: 'italic',
                                                  display: 'block',
                                                  mt: 1,
                                                  pt: 1,
                                                  borderTop: '1px solid #f0f0f0'
                                                }}>
                                                  * Final shipping amount will be calculated post order confirmation
                                                </Typography>
                                              )}
                                            </>
                                          );
                                        })()}
                                      </Box>
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton size="small" sx={{ p: 0.5, ml: 0.5 }}>
                                      <InfoIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {/* Show "To be calculated" until order is validated (AWAITING PAYMENT or later) */}
                                {(() => {
                                  const isOrderValidated = currentStatus === 'AWAITING PAYMENT' || 
                                                           currentStatus === 'PAID' || 
                                                           currentStatus === 'VENDOR ORDER PROCESSED' || 
                                                           currentStatus === 'ASSEMBLING ORDER' || 
                                                           currentStatus === 'SHIPPED' || 
                                                           currentStatus === 'DELIVERED';
                                  
                                  // If order is NOT validated yet, show "To be calculated"
                                  if (!isOrderValidated) {
                                    return "To be calculated";
                                  }
                                  
                                  // If order is validated, use shipping_cost from backend (same as email template)
                                  // Note: 0 is a valid value, not "not set"
                                  return `$${(order?.shipping_cost || 0).toFixed(2)}`;
                                })()}
                              </Typography>
                            </Box>
                            {/* Disclaimer - show when "Final Shipping to be Calculated" is displayed */}
                            {shippingBreakdown?.has_manual_quotes && 
                             currentStatus !== 'AWAITING PAYMENT' && 
                             currentStatus !== 'PAID' && 
                             currentStatus !== 'VENDOR ORDER PROCESSED' && 
                             currentStatus !== 'ASSEMBLING ORDER' && 
                             currentStatus !== 'SHIPPED' && 
                             currentStatus !== 'DELIVERED' && (
                              <Typography variant="caption" sx={{ 
                                color: '#ff9800', 
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                                display: 'block',
                                mt: 0.5,
                                ml: 0
                              }}>
                                * Final shipping amount will be calculated post order confirmation
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>Discount:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ${(order?.discount_amount || 0).toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>VAT:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {(() => {
                                const isOrderValidated = currentStatus === 'AWAITING PAYMENT' || 
                                                         currentStatus === 'PAID' || 
                                                         currentStatus === 'VENDOR ORDER PROCESSED' || 
                                                         currentStatus === 'ASSEMBLING ORDER' || 
                                                         currentStatus === 'SHIPPED' || 
                                                         currentStatus === 'DELIVERED';
                                
                                // If order is validated, use tax_amount from backend (same as email template)
                                // Note: 0 is a valid value (for non-Mauritius countries), not "not set"
                                if (isOrderValidated) {
                                  return `$${(order?.tax_amount || 0).toFixed(2)}`;
                                }
                                
                                // If order not validated, show tax from order
                                return `$${(order?.tax_amount || 0).toFixed(2)}`;
                              })()}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 1.5 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {Number(order?.credit_amount || 0) > 0 ? 'Order Total:' : 'Total:'}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {(() => {
                                const isOrderValidated = currentStatus === 'AWAITING PAYMENT' || 
                                                         currentStatus === 'PAID' || 
                                                         currentStatus === 'VENDOR ORDER PROCESSED' || 
                                                         currentStatus === 'ASSEMBLING ORDER' || 
                                                         currentStatus === 'SHIPPED' || 
                                                         currentStatus === 'DELIVERED';
                                
                                // If order is validated, use total_amount from backend (same as email template)
                                if (isOrderValidated) {
                                  // Check if any item is ON_DEMAND for display purposes
                                  const hasOnDemandItems = order?.items?.some(item => {
                                    const unitPriceNum = Number(item.unit_price ?? item.product_price ?? 0);
                                    return (item.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
                                           (item.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
                                  });
                                  
                                  // Show "Pending" if ON_DEMAND items exist (even after validation)
                                  if (hasOnDemandItems) {
                                    return <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>Pending</Typography>;
                                  }
                                  
                                  // Use backend total_amount (same as email template)
                                  return `$${(order?.total_amount || 0).toFixed(2)}`;
                                }
                                
                                // If order not validated, check for ON_DEMAND items
                                const hasOnDemandItems = order?.items?.some(item => {
                                  const unitPriceNum = Number(item.unit_price ?? item.product_price ?? 0);
                                  return (item.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
                                         (item.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
                                });
                                
                                if (hasOnDemandItems) {
                                  return <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>Pending</Typography>;
                                }
                                
                                // If order not validated, show "To be calculated" (same as shipping)
                                // This ensures total shows "To be calculated" until admin validates the order
                                if (!isOrderValidated) {
                                  return <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>To be calculated</Typography>;
                                }
                                
                                // If order is validated, show actual total
                                return `$${(order?.total_amount || 0).toFixed(2)}`;
                              })()}
                            </Typography>
                          </Box>
                          {/* Credit Applied Section */}
                          {Number(order?.credit_amount || 0) > 0 && (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="body1" sx={{ color: '#2e7d32' }}>Credit Applied:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                  -${Number(order.credit_amount).toFixed(2)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Balance Due:</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                  ${Math.max(0, (Number(order?.total_amount || 0) - Number(order?.credit_amount || 0))).toFixed(2)}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </SectionCard>

              {/* Payment Status ONLY (small chip) */}
              <SectionCard>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Payment Status</h3>
                  <PaymentStatusChip isPaid={isPaid} statusText={getPaymentStatusDisplay()} />
                </div>
              </SectionCard>

              {/* Order details & documents — button now says "Download Documents" */}
              <SectionCard>
                <OrderDetailsDocuments order={order} />
              </SectionCard>

              {/* Cancel Order Dialog */}
              <Dialog 
                open={cancelDialogOpen} 
                onClose={() => !cancelling && (setCancelDialogOpen(false), setCancelReason(''), setCustomCancelReason(''))} 
                maxWidth="sm" 
                fullWidth
              >
                <DialogTitle>Cancel Order</DialogTitle>
                <DialogContent>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ mb: 2 }}>Please select a reason for cancellation:</FormLabel>
                    <RadioGroup value={cancelReason} onChange={handleRadioChange}>
                      {CANCELLATION_REASONS.map((reason) => (
                        <FormControlLabel key={reason} value={reason} control={<Radio />} label={reason} />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Or enter a custom reason:
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Enter custom cancellation reason..."
                      value={customCancelReason}
                      onChange={handleCustomTextChange}
                      onFocus={handleCustomTextFocus}
                      sx={{ 
                        '& .MuiInputBase-root': {
                          backgroundColor: '#fff',
                        }
                      }}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button 
                    onClick={() => {
                      setCancelDialogOpen(false);
                      setCancelReason('');
                      setCustomCancelReason('');
                    }} 
                    disabled={cancelling}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCancelOrder} 
                    variant="contained" 
                    color="error" 
                    disabled={!hasValidReason || cancelling}
                  >
                    {cancelling ? 'Cancelling...' : 'Submit'}
                  </Button>
                </DialogActions>
              </Dialog>
            </VerticalPaddingContainer>
          </ScrollableSection>
        </ContentContainer>
      </BodyWrapper>
    </PageContainer>
  );
}
